import { useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  Checkbox,
  useDisclosure,
  Divider,
  Badge,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from "@chakra-ui/react";

const dxLevels = ["Extensive", "Moderate", "Limited", "Minimal or none"];

const dxOptions = {
  "Minimal or none": {
    type: "single",
    options: ["Minimal or none"]
  },
  Limited: {
    type: "grouped",
    categories: {
      "Category 1: Tests and documents": [
        "Review of prior external note(s) from each unique source*",
        "Review of the result(s) of each unique test*",
        "Ordering of each unique test*"
      ],
      "Category 2: Assessment requiring an independent historian(s)": []
    }
  },
  Moderate: {
    type: "grouped",
    categories: {
      "Category 1: Tests, documents, or independent historian(s)": [
        "Review of prior external note(s) from each unique source*",
        "Review of the result(s) of each unique test*",
        "Ordering of each unique test*",
        "Assessment requiring an independent historian(s)"
      ],
      "Category 2: Independent interpretation of tests": [
        "Independent interpretation of a test performed by another physician/qualified professional (not separately reported)"
      ],
      "Category 3: Discussion of management or test interpretation": [
        "Discussion of management/test interpretation with external physician/qualified professional (not separately reported)"
      ]
    }
  },
  Extensive: {
    type: "grouped",
    categories: {
      "Category 1: Tests, documents, or independent historian(s)": [
        "Review of prior external note(s) from each unique source*",
        "Review of the result(s) of each unique test*",
        "Ordering of each unique test*",
        "Assessment requiring an independent historian(s)"
      ],
      "Category 2: Independent interpretation of tests": [
        "Independent interpretation of a test performed by another physician/qualified professional (not separately reported)"
      ],
      "Category 3: Discussion of management or test interpretation": [
        "Discussion of management/test interpretation with external physician/qualified professional/appropriate source (not separately reported)"
      ]
    }
  }
};

export default function DXOptionsSelector({ value, onChange }) {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedCategories, setSelectedCategories] = useState({});
  const [inputValue, setInputValue] = useState(value || "");
  const [previewText, setPreviewText] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Update preview when selections change (only if modal is open)
  useEffect(() => {
    if (isOpen && hasSelections()) {
      const structuredText = generateStructuredText();
      if (structuredText) {
        setPreviewText(structuredText);
      }
    }
  }, [selectedOptions, selectedCategories, isOpen]);

  // When modal opens, set preview to current input value
  useEffect(() => {
    if (isOpen) {
      setPreviewText(inputValue);
    }
  }, [isOpen, inputValue]);

  const handleOptionChange = (level, category, item) => {
    setSelectedOptions((prev) => {
      const categoryKey = `${level}-${category}`;
      const current = prev[categoryKey] || [];
      const updated = current.includes(item)
        ? current.filter((opt) => opt !== item)
        : [...current, item];

      const newState = { ...prev, [categoryKey]: updated };

      // Auto-select category if any item is selected
      if (updated.length > 0) {
        setSelectedCategories(prevCat => ({
          ...prevCat,
          [categoryKey]: true
        }));
      } else {
        // Remove category selection if no items are selected
        setSelectedCategories(prevCat => {
          const newCat = { ...prevCat };
          delete newCat[categoryKey];
          return newCat;
        });
      }

      return newState;
    });
  };

  const handleSingleOptionChange = (level, item) => {
    setSelectedOptions((prev) => {
      const levelKey = level;
      const current = prev[levelKey] || [];
      const updated = current.includes(item)
        ? []
        : [item];
      return { ...prev, [levelKey]: updated };
    });
  };

  const handleCategoryChange = (level, category) => {
    const categoryKey = `${level}-${category}`;
    const isSelected = selectedCategories[categoryKey];

    if (isSelected) {
      // Deselect category and all its items
      setSelectedCategories(prev => {
        const newState = { ...prev };
        delete newState[categoryKey];
        return newState;
      });
      setSelectedOptions(prev => {
        const newState = { ...prev };
        delete newState[categoryKey];
        return newState;
      });
    } else {
      // Select category (but don't auto-select items)
      setSelectedCategories(prev => ({
        ...prev,
        [categoryKey]: true
      }));
    }
  };

  // Helper function to generate structured text from selections
  const generateStructuredText = () => {
    const selectedText = [];

    dxLevels.forEach(level => {
      const config = dxOptions[level];

      if (config.type === "single") {
        const levelOptions = selectedOptions[level] || [];
        if (levelOptions.length > 0) {
          selectedText.push(`${level} - ${levelOptions.join("; ")}`);
        }
      } else if (config.type === "grouped") {
        const levelSelections = [];

        Object.keys(config.categories).forEach(category => {
          const categoryKey = `${level}-${category}`;
          const categorySelected = selectedCategories[categoryKey];
          const itemsSelected = selectedOptions[categoryKey] || [];

          if (categorySelected && itemsSelected.length === 0) {
            // Category selected but no specific items
            levelSelections.push(category);
          } else if (itemsSelected.length > 0) {
            // Specific items selected
            levelSelections.push(`${category}: [${itemsSelected.join("; ")}]`);
          }
        });

        if (levelSelections.length > 0) {
          selectedText.push(`${level} - ${levelSelections.join("; ")}`);
        }
      }
    });

    // Filter out empty strings from selectedText
    const validSelectedText = selectedText.filter(text => text && text.trim());
    return validSelectedText.join(", ");
  };

  const handleApply = () => {
    // Apply exactly what's in the preview
    const finalValue = previewText.trim();
    setInputValue(finalValue);
    onChange?.(finalValue);
    onClose();
  };

  const handleClear = () => {
    setSelectedOptions({});
    setSelectedCategories({});
    setPreviewText("");
  };

  const handleModalClose = () => {
    // Clear selections when closing/canceling to reset state for next time
    setSelectedOptions({});
    setSelectedCategories({});
    onClose();
  };

  const hasSelections = () => {
    return Object.values(selectedOptions).some(level => level && level.length > 0) ||
      Object.values(selectedCategories).some(category => category);
  };

  const renderGroupedLevel = (level) => {
    const config = dxOptions[level];

    const instructionMap = {
      Limited: "Must meet the requirements of at least 1 of the 2 categories",
      Moderate: "Must meet the requirements of at least 1 out of 3 categories",
      Extensive: "Must meet the requirements of at least 2 out of 3 categories"
    };

    return (
      <Box key={level} mb={6}>
        <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
          {level}
        </Text>

        {instructionMap[level] && (
          <Text fontSize="sm" mb={3} color="gray.600" fontStyle="italic">
            {instructionMap[level]}
          </Text>
        )}

        <VStack align="start" spacing={2} pl={2}>
          {Object.entries(config.categories).map(([category, items]) => {
            const categoryKey = `${level}-${category}`;
            const isCategorySelected = selectedCategories[categoryKey];
            const selectedItems = selectedOptions[categoryKey] || [];

            return (
              <Box key={category} w="100%" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                <HStack justify="space-between" mb={2}>
                  <Checkbox
                    isChecked={!!isCategorySelected}
                    onChange={() => handleCategoryChange(level, category)}
                    fontWeight="semibold"
                    colorScheme="green"
                  >
                    {category}
                  </Checkbox>
                  {selectedItems.length > 0 && (
                    <Badge colorScheme="blue" variant="subtle">
                      {selectedItems.length} selected
                    </Badge>
                  )}
                </HStack>

                {items.length > 0 && (
                  <VStack align="start" spacing={2} pl={6}>
                    {items.map((item) => (
                      <Checkbox
                        key={item}
                        size="sm"
                        isChecked={selectedItems.includes(item)}
                        onChange={() => handleOptionChange(level, category, item)}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">{item}</Text>
                      </Checkbox>
                    ))}
                  </VStack>
                )}
              </Box>
            );
          })}
        </VStack>
      </Box>
    );
  };

  const renderSingleLevel = (level) => {
    const config = dxOptions[level];
    const selectedItems = selectedOptions[level] || [];

    return (
      <Box key={level} mb={6}>
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="bold" fontSize="lg" color="blue.600">
            {level}
          </Text>
          {selectedItems.length > 0 && (
            <Badge colorScheme="blue" variant="subtle">
              {selectedItems.length} selected
            </Badge>
          )}
        </HStack>
        <VStack align="start" spacing={2} pl={2}>
          {config.options.map((item) => (
            <Checkbox
              key={item}
              isChecked={selectedItems.includes(item)}
              onChange={() => handleSingleOptionChange(level, item)}
              colorScheme="blue"
            >
              {item}
            </Checkbox>
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <FormControl>
      <FormLabel>No. of dx options reviewed</FormLabel>
      <HStack spacing={2} align="center">
        <Input
          placeholder="Type or select options..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange?.(e.target.value);
          }}
        />
        <Button size="sm" variant="outline" onClick={onOpen}>
          Select
        </Button>
      </HStack>

      <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent maxH="85%" maxW="75%" w="auto" minW="40%" overflowY="auto" overflowX="hidden" borderRadius="md">
          <ModalHeader>Select Data Complexity Options</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {dxLevels.map((level) => {
              const config = dxOptions[level];

              return (
                <Box key={level}>
                  {config.type === "single"
                    ? renderSingleLevel(level)
                    : renderGroupedLevel(level)
                  }
                  {level !== dxLevels[dxLevels.length - 1] && <Divider my={4} />}
                </Box>
              );
            })}

            <Divider my={4} />
            <Accordion allowToggle mb={3}>
              <AccordionItem border="none">
                <h2>
                  <AccordionButton px={0} py={1} _hover={{ bg: "transparent" }} display="inline-flex" alignItems="center">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mr={2}>
                      Instructions
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel px={0} pt={2} pb={1} fontSize="sm" color="gray.600" lineHeight="1.7">
                  • Use the options above to generate structured text automatically.<br />
                  • The result appears below in the <b>Preview</b> section and can be customized manually.<br />
                  • <Text as="span" fontWeight="semibold" color="red.400">Important:</Text> Make manual changes only <i>after</i> selecting options — new selections will overwrite existing text.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Editable Live Preview Box */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="bold" fontSize="lg" color="green.600">
                  Preview
                </Text>
                <HStack spacing={2}>
                  <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                    Editable
                  </Badge>
                  {hasSelections() && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => setPreviewText(generateStructuredText())}
                    >
                      Reset to Selected
                    </Button>
                  )}
                </HStack>
              </HStack>

              <Box
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="green.50"
                position="relative"
                _focusWithin={{
                  borderColor: "gray.400",
                  bg: "green.25"
                }}
              >
                <Textarea
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Select options above or type directly here.."
                  minH="100px"
                  maxH="200px"
                  resize="vertical"
                  border="none"
                  bg="transparent"
                  fontSize="sm"
                  lineHeight="1.6"
                  color="gray.700"
                  _focus={{
                    boxShadow: "none",
                    outline: "none"
                  }}
                  _placeholder={{
                    color: "gray.400",
                    fontStyle: "italic"
                  }}
                />
              </Box>

              <HStack justify="space-between" mt={2}>
                <Text fontSize="xs" color="gray.500">
                  Edit directly or use options above • This content will be saved exactly as shown
                </Text>
                <Text fontSize="xs" color="blue.500" fontWeight="medium">
                  {previewText ? `${previewText.length} characters` : ""}
                </Text>
              </HStack>
            </Box>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button variant="ghost" colorScheme="red" onClick={handleClear}>
              Clear
            </Button>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleApply}>
                Apply
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
}